import { getLogger } from '@omnia/ui/src/lib/services/logger/logger.service'
import type { I18N } from '@vista-digital/i18n'
import type { CompletedOrder, SubscriptionBillingOption } from '@vista-digital/ocapi-types/v1'
import {
    getLowestSubscriptionBillingOptionPrice,
    getSubscriptionBillingOptionById,
    loadCompletedOrderById,
    orderDomainStore,
} from '@vista-digital/occ-domain'
import { gtm } from '@/common/services/gtm/gtm.service'
import { CurzonEcommerceDataLayer, MembershipItem } from '@/common/services/models-gtm'
import { getSelectedSubscriptionBillingOption } from '@/features/membership/utils/subscriptions'
import { isCompletedSubscriptionOrder } from '../../../utils/isCompletedSubscriptionOrder'

const getLastCompletedOrder = (): CompletedOrder | undefined => {
    return (orderDomainStore.data as { lastCompletedOrder?: CompletedOrder }).lastCompletedOrder
}

export class SubscriptionsGtmEvents {
    private static get logger() {
        return getLogger('SubscriptionsGtmEvents').value
    }

    public static async addToCart(i18n: I18N) {
        try {
            const item = this.getEcommerceMembershipItem(i18n)
            if (!item) return
            const gtmData = new CurzonEcommerceDataLayer('add_to_cart', { items: [item] })
            await gtm.sendEcommerceDataLayer(gtmData)
        } catch (e) {
            this.logger.error('Error adding to cart', e)
        }
    }

    public static async voucherAddToCart(billingOption: SubscriptionBillingOption, i18n: I18N) {
        const { name } = billingOption
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('add_to_cart', {
                items: [
                    new MembershipItem({
                        item_name: i18n.translatable(name),
                        price: 0,
                        item_category: 'Membership',
                        quantity: 1,
                    }),
                ],
            })
        )
    }

    public static async beginCheckout(i18n: I18N) {
        const item = this.getEcommerceMembershipItem(i18n)
        if (!item) return
        await gtm.sendEcommerceDataLayer(new CurzonEcommerceDataLayer('begin_checkout', { items: [item] }))
    }

    public static async sendPurchaseEvent(orderId: string, i18n: I18N) {
        let completedOrder: CompletedOrder | undefined = await loadCompletedOrderById(orderId)

        if (!isCompletedSubscriptionOrder(completedOrder)) {
            completedOrder = getLastCompletedOrder()
        }
        if (!isCompletedSubscriptionOrder(completedOrder)) {
            this.logger.warn('Membership purchase event skipped. Completed subscription order was not found.', {
                orderId,
            })
            return
        }

        const { subscription, totalPrice } = completedOrder
        const subscriptionBillingOption = getSubscriptionBillingOptionById(subscription.subscriptionBillingOptionId)

        const total = totalPrice.valueIncludingTax ?? 0.0

        const item: MembershipItem = {
            item_name: subscriptionBillingOption ? i18n.translatable(subscriptionBillingOption.name) : 'Membership',
            price: total,
            item_category: 'Membership',
            quantity: 1,
        }
        await gtm.sendEcommerceDataLayer(
            new CurzonEcommerceDataLayer('purchase', { transaction_id: orderId, value: total, items: [item] })
        )
    }

    private static getEcommerceMembershipItem(i18n: I18N) {
        const subscriptionBillingOption = getSelectedSubscriptionBillingOption()
        if (!subscriptionBillingOption) return
        return new MembershipItem({
            item_name: i18n.translatable(subscriptionBillingOption.name),
            price:
                getLowestSubscriptionBillingOptionPrice(subscriptionBillingOption.prices)?.price.valueIncludingTax || 0,
            item_category: 'Membership',
            quantity: 1,
        })
    }
}
